import { cx } from '@linaria/core';
import { useRef, useEffect, cloneElement, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2Transitions } from '@pm2/design-tokens';
import { withSuspenseCache } from '@pm2/react-utils';
import { pm2SitemapStore, findRenderedDescriptorByPath, recursivelyFindParentBreadcrumb, findSubNavigationRoot, getResourcesForPage, recursivelyFindResourceById } from '@pm2/sitemap-utils';
import { pm2ConfigStore, pm2InterfaceStore } from '@pm2/store';
import { useLocation, Link } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { Tooltip } from '@ui/kit';
import { printHtmlToJsx, simpleHtmlParsers } from '@utils/ast';
import { throttleToAnimationFrame } from '@utils/common';
import { useMatchBreakpoint, CSSRefTransition, useToggleState, useClickOutsideListener, useKatex, Chevron } from '@utils/react';
import { MobileSubNavigation } from '../../sub-navigation/sub-navigation.mobile.js';
import { FavouriteButton } from './atom.favourite-button.js';
import { ResourceButton } from './atom.resource-button.js';
import { container, transitionWrapperCls, wrapperCls, CROSSFADE_DURATION_MS, breadcrumbsCls, linkCls, truncateCls, outlineCls, subNavigationButtonCls, labelCls, chevronCls, breadcrumbCls } from './root.breadcrumb.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function Breadcrumb() {
  const location = useLocation();
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const activePage = findRenderedDescriptorByPath(sitemap, location.pathname);
  let breadcrumbs = recursivelyFindParentBreadcrumb(sitemap, activePage);

  // if a resource was specifically requested, then push the active page to the
  // list of breadcrumbs, and replace the leaf node with the resource descriptor
  // instead
  const resourceId = location.searchParams.get("resource");
  if (activePage && resourceId) {
    breadcrumbs.push(activePage);
  }
  const mobileSubNavigationRef = useRef(null);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);

  // show only breadcrumbs with visible titles
  breadcrumbs = breadcrumbs.filter(it => it.title.trim().length > 0);

  // In mobile mode we don't bother rendering our breadcrumbs in case we only
  // have one level since it'll match the path rendered in the header.
  // However, if it has children we must render it since the SubNavigation is
  // located here!
  const showBreadcrumbMobile = breadcrumbs.length > 1 || !!activePage?.hasChildren || !!activePage?.hasResources;
  const subNav = useMobileSubnavigationTrigger(activePage);
  const showCourseFrontpages = pm2ConfigStore.useSelector(state => state.config.settings.showCourseFrontpages) && activePage?.type === "Course";

  // ... and generally we render our breadcrumbs as long as there are some,
  // except if we're on a course frontpage which inherently displays breadcrumbs
  // differently.
  const showBreadcrumb = showCourseFrontpages ? false : isMobile && showBreadcrumbMobile ? true : !!activePage && breadcrumbs.length > 0;
  const subNavigationRoot = findSubNavigationRoot(sitemap, location.pathname);
  const showResources = pm2InterfaceStore.useSelector(state => state.showResources);
  useEffect(() => {
    document.documentElement.classList.toggle("withBreadcrumb", showBreadcrumb);
  }, [showBreadcrumb]);
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: showBreadcrumb && /*#__PURE__*/jsx(CSSRefTransition, {
      timeout: pm2Transitions.headerCollapse.defaultDurationMs,
      children: ref => /*#__PURE__*/jsxs("div", {
        ref: ref,
        className: cx(wrapperCls, subNav.active && "openSubnav", showResources && "showResources"),
        children: [/*#__PURE__*/jsx("div", {
          className: container,
          children: /*#__PURE__*/jsxs(TransitionGroup, {
            className: transitionWrapperCls,
            children: [/*#__PURE__*/jsx(BreadcrumbContent, {
              showBreadcrumb: showBreadcrumb,
              activePage: activePage,
              resourceId: resourceId,
              breadcrumbs: breadcrumbs,
              subNav: subNav,
              mobileSubNavigationRef: mobileSubNavigationRef
            }, activePage?.id ?? ""), /*#__PURE__*/jsx(ResourceButton, {
              activePage: activePage
            }), /*#__PURE__*/jsx(FavouriteButton, {
              descriptor: activePage
            })]
          })
        }), !subNav.disabled && /*#__PURE__*/jsx(MobileSubNavigation, {
          enabled: subNav.active,
          currentPageDescriptor: activePage,
          subNavigationRoot: subNavigationRoot,
          collapseNavigation: subNav.collapse,
          rootRef: mobileSubNavigationRef
        })]
      })
    })
  });
}
function BreadcrumbContent({
  activePage,
  resourceId,
  breadcrumbs,
  showBreadcrumb,
  mobileSubNavigationRef,
  subNav,
  ...transitionProps
}) {
  const ref = useRef(null);
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  let leafPage = activePage;
  if (activePage && resourceId) {
    const resources = withSuspenseCache(getResourcesForPage(configStore, sitemapStore, activePage));
    leafPage = recursivelyFindResourceById(resources, resourceId ?? "");
  }
  const maxWidth = `${100 / (breadcrumbs.length + 1)}%`;
  useClickOutsideListener(subNav.collapse, [ref, mobileSubNavigationRef], {
    useCapturePhase: true,
    disabled: !showBreadcrumb || !subNav.active || subNav.disabled
  });
  useKatex(ref, [activePage?.id]);
  return /*#__PURE__*/jsx(CSSRefTransition, {
    ...transitionProps,
    classNames: breadcrumbsCls,
    timeout: CROSSFADE_DURATION_MS,
    nodeRef: ref,
    children: ref => /*#__PURE__*/jsxs("ul", {
      ref: ref,
      className: breadcrumbsCls,
      children: [breadcrumbs.map(breadcrumb => /*#__PURE__*/jsx(BreadcrumbItemWrapper, {
        maxWidth: maxWidth,
        tooltip: breadcrumb.title,
        children: breadcrumb.type === "ChapterGroup" || breadcrumb.type === "UserArea" ? /*#__PURE__*/jsx("span", {
          className: cx(linkCls, truncateCls, "chapterGroup"),
          children: printHtmlToJsx(breadcrumb.title, simpleHtmlParsers)
        }) : /*#__PURE__*/jsxs(Link, {
          href: breadcrumb.path,
          className: linkCls,
          children: [/*#__PURE__*/jsx("span", {
            className: truncateCls,
            children: printHtmlToJsx(breadcrumb.title, simpleHtmlParsers)
          }), /*#__PURE__*/jsx("span", {
            className: outlineCls
          })]
        })
      }, breadcrumb.id)), leafPage && /*#__PURE__*/jsx(BreadcrumbItemWrapper, {
        className: cx(!subNav.disabled && "hasChevron"),
        maxWidth: maxWidth,
        tooltip: leafPage.title,
        children: subNav.disabled ? /*#__PURE__*/jsx("span", {
          className: truncateCls,
          children: printHtmlToJsx(leafPage.title, simpleHtmlParsers)
        }) : /*#__PURE__*/jsxs("button", {
          "aria-label": "\xC5bn navigationsmenu",
          className: subNavigationButtonCls,
          onClick: subNav.toggle,
          children: [/*#__PURE__*/jsx("span", {
            className: labelCls,
            children: printHtmlToJsx(leafPage.title, simpleHtmlParsers)
          }), /*#__PURE__*/jsx("span", {
            className: chevronCls,
            children: /*#__PURE__*/jsx(Chevron, {
              vertical: true,
              size: 8,
              lineWidth: 1.5,
              inversed: subNav.active
            })
          })]
        })
      })]
    })
  });
}
function BreadcrumbItemWrapper({
  className,
  tooltip,
  children
}) {
  const ref = useRef(null);
  const hasOverflow = useHasOverflow(ref);
  return /*#__PURE__*/jsx("li", {
    className: cx(breadcrumbCls, className),
    children: /*#__PURE__*/jsx(Tooltip, {
      containerRef: ref,
      placement: "bottom",
      body: printHtmlToJsx(tooltip, simpleHtmlParsers),
      ariaType: undefined,
      disabled: !tooltip || !hasOverflow,
      offsetPx: 6,
      children: ref => /*#__PURE__*/cloneElement(children, {
        ref
      })
    })
  });
}
function useMobileSubnavigationTrigger(activePage) {
  const location = useLocation();
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const showCourseFrontpagesSetting = pm2ConfigStore.useSelector(state => state.config.settings.showCourseFrontpages);
  const showCourseFrontpages = showCourseFrontpagesSetting && activePage?.type === "Course";
  const subNavigationRoot = findSubNavigationRoot(sitemap, location.pathname);
  const disabled = showCourseFrontpages || !isMobile || !subNavigationRoot?.children?.length;
  const [active, actions] = useToggleState(false);
  useEffect(() => {
    if (!isMobile || !subNavigationRoot || showCourseFrontpagesSetting) {
      return;
    }

    // auto-expand if we've accessed a course front page
    if (subNavigationRoot?.path === location.pathname && subNavigationRoot.type === "Course") {
      actions.enable();
    }
  }, [isMobile, subNavigationRoot, location.pathname, showCourseFrontpagesSetting, actions]);
  return {
    active,
    disabled,
    toggle: actions.toggle,
    collapse: actions.disable
  };
}
function useHasOverflow(ref) {
  const [hasOverflow, setOverflow] = useState(false);
  useEffect(() => {
    const node = ref.current?.querySelector(`.${truncateCls}`) ?? ref.current;
    if (!node) {
      return;
    }
    const determineOverflowState = () => {
      setOverflow(node.scrollWidth > node.clientWidth);
    };
    determineOverflowState();
    const handleResize = throttleToAnimationFrame(determineOverflowState);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [ref]);
  return hasOverflow;
}

export { Breadcrumb };
//# sourceMappingURL=root.breadcrumb.js.map
